import clsx from 'clsx';
import { memo, type KeyboardEvent, type PointerEvent } from 'react';

import type { OptionProps } from '../types';

import { Icon } from '../../Icon/Icon';
import { TextBlock } from '../../TextBlock/TextBlock';
import { useListboxItemRef } from '../hooks';
import { areRowPropsEqual, preventFocusSteal } from '../utils';
import './Option.css';

function OptionComponent<V = string>({
  active,
  id,
  index,
  option,
  selected,
  onSelect,
  __private,
}: OptionProps<V>) {
  const { elementsRef, isKeyboardFocused = false, onHoverIndex } = __private;
  const { disabled, icon, menuText, secondaryText, text } = option;
  const ref = useListboxItemRef(elementsRef, index);

  const handleClick = () => {
    if (!disabled) {
      onSelect(option);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(option);
    }
  };

  const handleMouseMove = () => {
    onHoverIndex(index);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') {
      onHoverIndex(null);
    }
  };

  return (
    <div
      aria-disabled={disabled || undefined}
      aria-selected={selected}
      className={clsx(
        'eds-select--option',
        selected && 'eds-select--option-selected',
        active && 'eds-select--option-active',
        isKeyboardFocused && 'eds-select--option-keyboard-focus',
        disabled && 'eds-select--option-disabled',
      )}
      id={id}
      ref={ref}
      role="option"
      tabIndex={-1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={preventFocusSteal}
      onMouseMove={handleMouseMove}
      onPointerLeave={handlePointerLeave}
    >
      <TextBlock
        icon={icon}
        secondaryText={secondaryText}
        secondaryTone={selected ? 'auto' : 'subdued'}
        text={menuText ?? text}
        truncate={1}
      />
      {selected ? (
        <span className="eds-select--option-check">
          <Icon icon="check" size="s" />
        </span>
      ) : null}
    </div>
  );
}

export const Option = memo(
  OptionComponent,
  areRowPropsEqual,
) as typeof OptionComponent & { displayName?: string };

Option.displayName = 'Option';
